//#region import libraries
import React, { useEffect, useMemo } from "react";
//bootstrap
import { Row, Col } from "react-bootstrap";
//moment
import moment from "moment";
//tremor
import {
    Title as TremorTitle,
    Subtitle,
    Text,
    Metric,
    Flex,
    DateRangePicker,
    DonutChart,
    Legend,
    Table,
    TableHead,
    TableRow,
    TableHeaderCell,
    TableBody,
    TableCell,
} from "@tremor/react";
//custom component
import { Card, CustomSpinner } from "_components/atoms";
//redux
import { useDispatch, useSelector } from "react-redux";
import { selectUserDb } from "_redux/slices/main-screen/user";
import {
    getSurveySendStats,
    selectSurveySendStats,
    selectSurveySendStatsStatus,
} from "_redux/slices/analytics/review-control-panel";
//#endregion

// Feature release date — survey-send tracking starts here, so there is no data before it.
// Used as both the default start date and the earliest selectable date.
// NB: built as LOCAL midnight (month is 0-indexed → 7 = August). Using new Date("2026-08-10")
// would parse as UTC midnight and, in a UTC+ timezone, disable today's cell in the picker.

const asPercent = (rate) => `${((rate || 0) * 100).toFixed(1)}%`;

const SurveyStats = () => {
    //#region redux
    const dispatch = useDispatch();
    const userDb = useSelector(selectUserDb);
    const stats = useSelector(selectSurveySendStats);
    const status = useSelector(selectSurveySendStatsStatus);
    //#endregion

    //#region state
    const [date, setDate] = React.useState([new Date(), new Date()]);
    //#endregion

    //#region useEffect
    useEffect(() => {
        if (userDb && date[0] && date[1]) {

            dispatch(
                getSurveySendStats({
                    iata: userDb?.details.authority,
                    start: moment(new Date(date[0])).format().split("T")[0],
                    end: moment(new Date(date[1])).format().split("T")[0],
                })
            );
        }
    }, [userDb, date]);
    //#endregion

    //#region derived
    const totals = stats?.totals || { sent: 0, answered: 0, responseRate: 0 };
    const notAnswered = Math.max(0, (totals.sent || 0) - (totals.answered || 0));

    const donutData = useMemo(
        () => [
            { name: "Answered", value: totals.answered || 0 },
            { name: "Not answered", value: notAnswered },
        ],
        [totals.answered, notAnswered]
    );

    const surveyRows = stats?.surveys || [];
    //#endregion

    const isLoading = status === "loading";
    const hasData = (totals.sent || 0) > 0;

    //#region rendering
    return (
        <>
            {/* Title + date filter */}
            <Row className="mt-4">
                <Col md={7}>
                    <TremorTitle>Survey delivery</TremorTitle>
                    <Subtitle>{"How many surveys were emailed and how many were answered"}</Subtitle>
                </Col>
                <Col md={5}>
                    <DateRangePicker
                        onValueChange={(value) => setDate(value)}
                        value={date}
                        enableDropdown={true}
                        maxWidth="max-w-lg"
                        dropdownPlaceholder={"Since release"}
                    />
                </Col>
            </Row>

            {/* Summary tiles + donut */}
            <Row className="mt-3">
                <Col md={5}>
                    <Card className="h-100">
                        <TremorTitle>Sent vs Answered</TremorTitle>
                        {isLoading ? (
                            <Row className="justify-content-md-center mt-5">
                                <CustomSpinner />
                            </Row>
                        ) : hasData ? (
                            <>
                                <DonutChart
                                    className="mt-4"
                                    data={donutData}
                                    category="value"
                                    index="name"
                                    colors={["emerald", "rose"]}
                                    valueFormatter={(v) => `${v}`}
                                />
                                <Legend
                                    className="mt-3"
                                    categories={["Answered", "Not answered"]}
                                    colors={["emerald", "rose"]}
                                />
                            </>
                        ) : (
                            <Text className="mt-4">No surveys were sent in this period.</Text>
                        )}
                    </Card>
                </Col>

                <Col md={7}>
                    <Card className="h-100">
                        <TremorTitle>Totals</TremorTitle>
                        <Flex marginTop="mt-4" justify="space-between">
                            <div>
                                <Text>Sent</Text>
                                <Metric>{totals.sent || 0}</Metric>
                            </div>
                            <div>
                                <Text>Answered</Text>
                                <Metric>{totals.answered || 0}</Metric>
                            </div>
                            <div>
                                <Text>Response rate</Text>
                                <Metric>{asPercent(totals.responseRate)}</Metric>
                            </div>
                        </Flex>
                    </Card>
                </Col>
            </Row>

            {/* Per-survey breakdown */}
            <Row className="mt-3" style={{ paddingBottom: 100 }}>
                <Col>
                    <Card>
                        <TremorTitle>By survey</TremorTitle>
                        <Table className="mt-4">
                            <TableHead>
                                <TableRow>
                                    <TableHeaderCell>Survey</TableHeaderCell>
                                    <TableHeaderCell>Sent</TableHeaderCell>
                                    <TableHeaderCell>Answered</TableHeaderCell>
                                    <TableHeaderCell>Response rate</TableHeaderCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {surveyRows.length ? (
                                    surveyRows.map((s) => (
                                        <TableRow key={s.surveyId || "unknown"}>
                                            <TableCell>{s.surveyId || "Unknown"}</TableCell>
                                            <TableCell>{s.sent || 0}</TableCell>
                                            <TableCell>{s.answered || 0}</TableCell>
                                            <TableCell>{asPercent(s.responseRate)}</TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell>{isLoading ? "Loading…" : "No data"}</TableCell>
                                        <TableCell>-</TableCell>
                                        <TableCell>-</TableCell>
                                        <TableCell>-</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Card>
                </Col>
            </Row>
        </>
    );
    //#endregion
};

export default SurveyStats;
